import { useRef, useState } from "react";
import { Button, Input, Select } from "@arco-design/web-react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, KeyRound, Link2 } from "lucide-react";
import type { Account } from "../shared/domain";
import { api, ApiError, post, setCsrf, showError } from "./api";
import { Logo } from "./components/Logo";
import { ThemeToggle } from "./theme";

export function ActivateAccess({ account, onRefresh, onLogout }: { account: Account; onRefresh: () => void; onLogout: () => void }) {
  const [inviteCode, setInviteCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState<"invite" | "link">(() => new URLSearchParams(location.search).has("bindExisting") ? "link" : "invite");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("");
  const pending = useRef(false), requestId = useRef(crypto.randomUUID());
  const options = useQuery({ queryKey: ["account-link-options", account.id],
    queryFn: () => api<{ enabled: boolean; requiresLogin: boolean; reauthUrl: string; providers: { name: string; label: string }[] }>("/auth/link-options"), retry: 1 });
  const provider = selectedProvider || options.data?.providers[0]?.name || "";
  const providerLabel = options.data?.providers.find(item => item.name === provider)?.label || "第三方";
  async function bind(event: React.FormEvent) {
    event.preventDefault();
    if (pending.current || !provider) return;
    pending.current = true;
    setBusy(true);
    setError("");
    const credentials = { username: username.trim(), password, provider, requestId: requestId.current };
    setPassword("");
    try {
      const result = await post<{ csrf: string; logoutUrl: string }>("/auth/link-existing", credentials);
      setCsrf(result.csrf);
      if (result.logoutUrl) location.assign(result.logoutUrl);
      else onRefresh();
    } catch (error) {
      setError(error instanceof Error ? error.message : "绑定失败，请重试");
      if (error instanceof ApiError && error.code === "FRESH_LOGIN_REQUIRED") options.refetch();
      pending.current = false;
      setBusy(false);
    }
  }
  async function activate(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await post("/auth/activate", { inviteCode: inviteCode.trim() });
      onRefresh();
    } catch (error) {
      setError(error instanceof Error ? error.message : "开通失败，请重试");
    } finally {
      setBusy(false);
    }
  }
  async function logout() {
    setBusy(true);
    try {
      const result = await post<{ logoutUrl: string | null }>("/auth/logout");
      setCsrf("");
      if (result.logoutUrl) location.assign(result.logoutUrl);
      else onLogout();
    } catch (error) {
      showError(error);
      setBusy(false);
    }
  }
  return <div className="auth-screen">
    <header className="auth-header">
      <a className="brand auth-brand" href="/"><Logo /> SUPER TODO</a>
      <ThemeToggle />
    </header>
    <section className="auth-form-wrap">
      <div className="auth-form">
        {mode === "link" ? <Link2 size={32} className="accent" /> : <KeyRound size={32} className="accent" />}
        <h2>{mode === "link" ? "绑定已有账号" : "开通使用权限"}</h2>
        <p className="muted">{mode === "link" ? "验证已有账号的密码，将当前第三方身份绑定到该账号，继续使用原有任务和权限。" : `你好，${account.username}。账号已登录，输入邀请码即可开始使用 SUPER TODO。`}</p>
        {options.isPending || options.isError || options.data?.enabled ? <div className="access-choices" role="tablist" aria-label="选择开通方式">
          <button type="button" role="tab" aria-selected={mode === "invite"} disabled={busy} onClick={() => { setMode("invite"); setPassword(""); setError(""); }}>输入邀请码</button>
          <button type="button" role="tab" aria-selected={mode === "link"} disabled={busy} onClick={() => { setMode("link"); setError(""); }}>绑定已有账号</button>
        </div> : null}
        {mode === "invite" ? <>
        <form onSubmit={activate}>
        <label htmlFor="access-invite-code">邀请码</label>
        <Input id="access-invite-code" value={inviteCode} onChange={value => { setInviteCode(value); setError(""); }}
          required maxLength={200} autoComplete="off" placeholder="输入邀请码" size="large"
          status={error ? "error" : undefined} aria-describedby={error ? "access-invite-error" : undefined} />
        {error ? <p id="access-invite-error" role="alert">{error}</p> : null}
        <Button htmlType="submit" className="primary-action" long size="large" loading={busy}>开通并进入 <ArrowRight size={16} /></Button>
        </form>
        <p className="muted">还没有邀请码？请联系管理员获取。</p>
        </> : options.isPending ? <p className="muted">正在读取第三方登录信息…</p>
          : options.isError ? <><p role="alert">无法读取绑定信息，请稍后重试。</p><Button onClick={() => options.refetch()}>重试</Button></>
          : options.data?.requiresLogin ? <>
            <p className="muted">为确认当前第三方身份，请先重新验证统一账号登录，完成后会返回这里。</p>
            <Button className="primary-action" long size="large" onClick={() => location.assign(options.data!.reauthUrl)}>验证当前登录并继续 <ArrowRight size={16} /></Button>
          </> : !options.data?.enabled || !options.data.providers.length ? <p className="muted">当前登录没有可绑定的第三方身份。请使用 Apple 或 GitHub 登录后再试。</p>
          : <form onSubmit={bind}>
            {options.data.providers.length > 1 ? <><label htmlFor="access-link-provider">要绑定的第三方账号</label>
              <Select id="access-link-provider" value={provider} disabled={busy} onChange={value => { setSelectedProvider(value); requestId.current = crypto.randomUUID(); setError(""); }}
                options={options.data.providers.map(item => ({ value: item.name, label: item.label }))} size="large" /></>
              : <p className="access-link-context">将当前 {providerLabel} 身份移至你指定的已有账号。</p>}
            <label htmlFor="access-link-username">已有账号</label>
            <Input id="access-link-username" value={username} required maxLength={200} autoComplete="username" placeholder="已有账号的用户名或邮箱" size="large" disabled={busy}
              onChange={value => { setUsername(value); requestId.current = crypto.randomUUID(); setError(""); }} />
            <label htmlFor="access-link-password">已有账号的密码</label>
            <Input.Password id="access-link-password" value={password} required maxLength={1024} autoComplete="current-password" placeholder="输入该账号的密码" size="large" disabled={busy}
              onChange={value => { setPassword(value); setError(""); }} status={error ? "error" : undefined} aria-describedby={error ? "access-link-error" : undefined} />
            {error ? <p id="access-link-error" role="alert">{error}</p> : null}
            <Button htmlType="submit" className="primary-action" long size="large" loading={busy}>验证并绑定 <ArrowRight size={16} /></Button>
            <p className="access-link-context">绑定后沿用目标账号的权限。若该账号也未开通，仍需输入邀请码。</p>
          </form>}
        <Button type="text" disabled={busy} onClick={logout}>退出并切换账号</Button>
      </div>
    </section>
  </div>;
}
